/*
  Wild Paws – shared add-to-cart helper.
  Any element with [data-wpc-add="<variantId>"] adds that variant (qty from [data-wpc-qty] if set).
  Dispatches Shopify's standard cart:lines-update event so Horizon's cart drawer, bubble and
  every free-delivery bar refresh together. Also defines <wpc-quick-add> (inline variant picker).
*/
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

async function fetchCart() {
  const res = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!res.ok) throw new Error(`Cart ${res.status}`);
  return res.json();
}

/**
 * Add a variant to the cart and notify the theme.
 * @param {{ variantId: string|number, quantity?: number, source: HTMLElement, properties?: object }} opts
 * @returns {Promise<object>} the updated cart
 */
export async function wpcAddToCart({ variantId, quantity = 1, source, properties }) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch(`${ROOT}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity, properties }] }),
    });
    const data = await res.json();
    const cart = await fetchCart();
    const didError = !res.ok || Boolean(data.status);

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'wpc-cart-api', didError },
    });
    document.dispatchEvent(new CustomEvent('wpc:cart-updated', { detail: { cart } }));

    if (didError) {
      source.dispatchEvent(new CartErrorEvent({ error: data.description || data.message || 'Could not add to cart', code: 'INVALID' }));
      throw new Error(data.description || data.message || 'Could not add to cart');
    }
    return cart;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

function showError(button, message) {
  const holder = button.closest('.wpc-product-card__actions, [data-wpc-error-scope]');
  const el = holder && holder.querySelector('.wpc-product-card__error, [data-wpc-error]');
  if (!el) return;
  el.textContent = message;
  el.hidden = false;
  clearTimeout(el._wpcTimer);
  el._wpcTimer = setTimeout(() => { el.hidden = true; }, 5000);
}

document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-wpc-add]') : null;
  if (!button || button.disabled || button.classList.contains('is-loading')) return;
  event.preventDefault();

  const qtyInput = button.dataset.wpcQty ? document.querySelector(button.dataset.wpcQty) : null;
  const quantity = Math.max(1, parseInt(qtyInput ? qtyInput.value : '1', 10) || 1);

  button.classList.add('is-loading');
  button.setAttribute('aria-disabled', 'true');
  try {
    await wpcAddToCart({ variantId: button.dataset.wpcAdd, quantity, source: button });
    const picker = button.closest('wpc-quick-add');
    if (picker) picker.close();
  } catch (error) {
    showError(button, error.message);
  } finally {
    button.classList.remove('is-loading');
    button.removeAttribute('aria-disabled');
  }
});

/* Free-delivery bars: keep every bar on the page in sync with the cart total. */
function updateDeliveryBars(cart) {
  document.querySelectorAll('[data-wpc-delivery-bar]').forEach((bar) => {
    const threshold = Number(bar.dataset.threshold || 4000);
    const total = cart.total_price;
    const remaining = Math.max(0, threshold - total);
    const pct = Math.min(100, Math.round((total / threshold) * 100));
    bar.style.setProperty('--wpc-progress', `${pct}%`);
    const msg = bar.querySelector('[data-wpc-delivery-message]');
    if (!msg) return;
    const fmt = (cents) =>
      new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: cart.currency || 'GBP' }).format(cents / 100);
    msg.innerHTML = remaining > 0
      ? `You're <strong>${fmt(remaining)}</strong> away from free delivery`
      : `<strong>You've unlocked free delivery</strong>`;
  });
}
document.addEventListener('wpc:cart-updated', (e) => updateDeliveryBars(e.detail.cart));
/* Minimal ajax-style cart ({ item_count, total_price, currency }) from a standard-events cart, or null. */
function cartFromEvent(cart) {
  const total = cart && cart.cost && cart.cost.totalAmount;
  if (!total || total.amount == null || cart.totalQuantity == null) return null;
  return { item_count: cart.totalQuantity, total_price: Math.round(parseFloat(total.amount) * 100), currency: total.currencyCode };
}

document.addEventListener('shopify:cart:lines-update', (e) => {
  if (!e.promise) return;

  /* Drawer quantity / remove: busy state on that line only (the component already blocks double clicks). */
  const component = e.target instanceof Element ? e.target.closest('cart-items-component') : null;
  const lineKey = e.context === 'cart' && e.lines && e.lines[0] ? String(e.lines[0].id || '') : '';
  const row = component && lineKey ? component.querySelector(`.cart-items__table-row[data-key="${CSS.escape(lineKey)}"]`) : null;
  if (row) row.classList.add('wpc-line-busy');
  const clearBusy = () => { if (row) row.classList.remove('wpc-line-busy'); };

  e.promise
    .then(({ cart, detail } = {}) => {
      clearBusy();
      if (detail && detail.source === 'wpc-cart-api') return; // already broadcast
      /* Broadcast straight from the response so bars + announcement update with the drawer, not a fetch later. */
      const quick = cartFromEvent(cart);
      if (quick) {
        document.dispatchEvent(new CustomEvent('wpc:cart-updated', { detail: { cart: quick } }));
        return;
      }
      return fetchCart().then((full) => document.dispatchEvent(new CustomEvent('wpc:cart-updated', { detail: { cart: full } })));
    })
    .catch(clearBusy);
});

class WpcQuickAdd extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-wpc-quick-add-toggle]');
    this.panel = this.querySelector('.wpc-quick-add__panel');
    this.onToggle = () => (this.panel.hidden ? this.open() : this.close());
    this.onOutside = (e) => { if (!this.contains(e.target)) this.close(); };
    this.onKey = (e) => { if (e.key === 'Escape') { this.close(); this.toggle.focus(); } };
    this.toggle.addEventListener('click', this.onToggle);
  }
  disconnectedCallback() {
    this.toggle && this.toggle.removeEventListener('click', this.onToggle);
    document.removeEventListener('click', this.onOutside);
    document.removeEventListener('keydown', this.onKey);
  }
  open() {
    this.panel.hidden = false;
    this.toggle.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', this.onOutside);
    document.addEventListener('keydown', this.onKey);
    const first = this.panel.querySelector('button:not([disabled])');
    if (first) first.focus({ preventScroll: true });
  }
  close() {
    this.panel.hidden = true;
    this.toggle.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.onOutside);
    document.removeEventListener('keydown', this.onKey);
  }
}
if (!customElements.get('wpc-quick-add')) customElements.define('wpc-quick-add', WpcQuickAdd);
